/**
 * Задачи: список, карточка, участники, чек-лист, напоминания.
 *
 * Типы описаны здесь, а не сгенерированы: генерация из OpenAPI даёт
 * `components["schemas"]["TaskPublic"]`, что нечитаемо в местах использования.
 * Совпадение с сервером держится тестом контракта.
 */

import { downloadFile, newIdempotencyKey, request } from "@/shared/api/client";
import type { Condition } from "@/shared/api/views";

export interface Members {
  assignees: number[];
  participants: number[];
  auditors: number[];
  counterparties: number[];
}

export interface Task {
  id: number;
  number: number;
  object_id: number;
  project_id: number | null;
  parent_id: number | null;
  status_id: number;
  name: string;
  description: string | null;
  assigner_id: number;
  /** На кого выставлен счёт: справочник компаний из настроек. */
  company_id: number | null;
  /** От кого пришёл счёт: справочник контрагентов. */
  counterparty_id: number | null;
  depth: number;
  start_at: string | null;
  end_at: string | null;
  completed_at: string | null;
  priority: number | null;
  email_key: string | null;
  comment_count: number;
  last_activity_at: string | null;
  is_deleted: boolean;
  cf: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  members: Members;
  is_favorite: boolean;
  unread_count: number;
  /** Битовая маска прав смотрящего — см. RIGHTS. */
  rights: number;
}

export interface TaskRow {
  id: number;
  number: number;
  name: string;
  status_id: number;
  project_id: number | null;
  /** Чья это работа: в справочнике компании зовут одной буквой — «Р», «М», «Д». */
  company_id: number | null;
  /** От кого пришёл счёт: колонка списка показывает его название. */
  counterparty_id: number | null;
  parent_id: number | null;
  assigner_id: number;
  assignees: number[];
  priority: number | null;
  start_at: string | null;
  end_at: string | null;
  comment_count: number;
  created_at: string;
  last_activity_at: string | null;
  unread_count: number;
  is_favorite: boolean;
  has_children: boolean;
}

export interface TaskPage {
  items: TaskRow[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface ChecklistItem {
  id: number;
  task_id: number;
  position: number;
  text: string;
  is_done: boolean;
  done_at: string | null;
  done_by_id: number | null;
  subtask_id: number | null;
}

export interface Reminder {
  id: number;
  task_id: number;
  user_id: number;
  remind_at: string;
  note: string | null;
  is_sent: boolean;
}

/** Биты из app/core/security/rights.py: интерфейс прячет недоступные действия. */
export const RIGHTS = {
  view: 1,
  comment: 2,
  edit: 4,
  changeStatus: 8,
  manageParticipants: 16,
  manageAnalytics: 32,
  delete: 64,
  viewPrivate: 128,
  viewInternal: 256,
} as const;

export function can(rights: number, right: number): boolean {
  return (rights & right) > 0;
}

export type SortKey = "id" | "number" | "end_at" | "priority" | "activity" | "name" | "relevance";

export interface TaskFilters {
  project_id?: number | null;
  include_subprojects?: boolean;
  status_id?: number[];
  assignee_id?: number[];
  assigner_id?: number;
  parent_id?: number;
  only_root?: boolean;
  favorite?: boolean;
  unread?: boolean;
  overdue?: boolean;
  trash?: boolean;
  q?: string;
  sort?: SortKey;
  ascending?: boolean;
}

export function toQuery(filters: TaskFilters, extra: Record<string, string | number> = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "" || value === false) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item));
      }
      continue;
    }
    params.set(key, String(value));
  }
  for (const [key, value] of Object.entries(extra)) {
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function listTasks(
  filters: TaskFilters,
  options: { limit?: number; cursor?: string | null } = {},
): Promise<TaskPage> {
  const extra: Record<string, string | number> = { limit: options.limit ?? 50 };
  if (options.cursor) {
    extra["cursor"] = options.cursor;
  }
  return request<TaskPage>(`/api/tasks${toQuery(filters, extra)}`);
}

export function countTasks(filters: TaskFilters): Promise<{ total: number }> {
  return request<{ total: number }>(`/api/tasks/count${toQuery(filters)}`);
}

export function getTask(taskId: number): Promise<Task> {
  return request<Task>(`/api/tasks/${taskId}`);
}

export interface TaskDraft {
  name: string;
  description?: string;
  project_id?: number | null;
  parent_id?: number | null;
  status_id?: number;
  company_id?: number | null;
  counterparty_id?: number | null;
  members?: Partial<Members>;
  start_at?: string | null;
  end_at?: string | null;
  priority?: number | null;
  /** Пользовательские поля по коду поля: `{"489": true}`. */
  cf?: Record<string, unknown>;
}

export function createTask(draft: TaskDraft): Promise<Task> {
  return request<Task>("/api/tasks", {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateTask(taskId: number, patch: Partial<TaskDraft>): Promise<Task> {
  return request<Task>(`/api/tasks/${taskId}`, { method: "PATCH", body: patch });
}

export function changeStatus(taskId: number, statusId: number): Promise<Task> {
  return request<Task>(`/api/tasks/${taskId}/status`, {
    method: "POST",
    body: { status_id: statusId },
    idempotencyKey: newIdempotencyKey(),
  });
}

export function addMembers(taskId: number, members: Partial<Members>): Promise<Members> {
  return request<Members>(`/api/tasks/${taskId}/members`, {
    method: "POST",
    body: { members },
  });
}

export function removeMembers(taskId: number, members: Partial<Members>): Promise<Members> {
  return request<Members>(`/api/tasks/${taskId}/members/remove`, {
    method: "POST",
    body: { members },
  });
}

export function moveTask(
  taskId: number,
  move: { project_id?: number | null; parent_id?: number | null },
): Promise<Task> {
  return request<Task>(`/api/tasks/${taskId}/move`, { method: "POST", body: move });
}

export function trashTask(taskId: number): Promise<{ moved_to_trash: number }> {
  return request<{ moved_to_trash: number }>(`/api/tasks/${taskId}`, { method: "DELETE" });
}

export function restoreTask(taskId: number): Promise<Task> {
  return request<Task>(`/api/tasks/${taskId}/restore`, { method: "POST" });
}

export function setFavorite(taskId: number, favorite: boolean): Promise<void> {
  return request<undefined>(`/api/tasks/${taskId}/favorite`, {
    method: favorite ? "POST" : "DELETE",
  });
}

export function markRead(taskId: number): Promise<void> {
  return request<undefined>(`/api/tasks/${taskId}/read`, { method: "POST" });
}

export interface BulkOperation {
  /** Выделенные строки. Пусто вместе с `all_matching` — операции не над чем работать. */
  task_ids?: number[];
  /** Взять всё, что попало в отбор, а не только выделенное. */
  all_matching?: boolean;
  conditions?: Condition[];
  logic?: string;
  q?: string;
  trash?: boolean;

  status_id?: number;
  end_at?: string;
  add_members?: Partial<Members>;
  remove_members?: Partial<Members>;
  project_id?: number | null;
  move_to_project?: boolean;
  to_trash?: boolean;
}

export interface BulkRunError {
  task_id: number;
  message: string;
}

/** Прогон массовой операции: выполняется фоном, за ним следят по идентификатору. */
export interface BulkRun {
  id: number;
  status: "pending" | "running" | "done" | "cancelled" | "failed";
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: BulkRunError[];
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export function isRunOver(run: BulkRun): boolean {
  return run.status === "done" || run.status === "cancelled" || run.status === "failed";
}

export function bulk(operation: BulkOperation): Promise<BulkRun> {
  return request<BulkRun>("/api/tasks/bulk", {
    method: "POST",
    body: operation,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function bulkProgress(runId: number): Promise<BulkRun> {
  return request<BulkRun>(`/api/tasks/bulk/${runId}`);
}

export function bulkCancel(runId: number): Promise<BulkRun> {
  return request<BulkRun>(`/api/tasks/bulk/${runId}/cancel`, { method: "POST" });
}

export function checklist(taskId: number): Promise<ChecklistItem[]> {
  return request<ChecklistItem[]>(`/api/tasks/${taskId}/checklist`);
}

export function addChecklistItem(taskId: number, text: string): Promise<ChecklistItem> {
  return request<ChecklistItem>(`/api/tasks/${taskId}/checklist`, {
    method: "POST",
    body: { text },
  });
}

export function updateChecklistItem(
  itemId: number,
  patch: { text?: string; is_done?: boolean; position?: number },
): Promise<ChecklistItem> {
  return request<ChecklistItem>(`/api/checklist/${itemId}`, { method: "PATCH", body: patch });
}

export function deleteChecklistItem(itemId: number): Promise<void> {
  return request<undefined>(`/api/checklist/${itemId}`, { method: "DELETE" });
}

export function checklistItemToSubtask(itemId: number): Promise<Task> {
  return request<Task>(`/api/checklist/${itemId}/to-subtask`, { method: "POST" });
}

export function reminders(taskId: number): Promise<Reminder[]> {
  return request<Reminder[]>(`/api/tasks/${taskId}/reminders`);
}

export function addReminder(
  taskId: number,
  reminder: { remind_at: string; note?: string; user_id?: number },
): Promise<Reminder> {
  return request<Reminder>(`/api/tasks/${taskId}/reminders`, {
    method: "POST",
    body: reminder,
  });
}

export function deleteReminder(reminderId: number): Promise<void> {
  return request<undefined>(`/api/reminders/${reminderId}`, { method: "DELETE" });
}

export function exportUrl(filters: TaskFilters, format: "csv" | "xlsx"): string {
  return `/api/tasks/export${toQuery(filters, { format })}`;
}

export function exportTasks(filters: TaskFilters, format: "csv" | "xlsx"): Promise<void> {
  return downloadFile(exportUrl(filters, format));
}
